// DNA CONNECT - Página de Login das Famílias
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Loader2, Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function DnaConnectLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Se já está logado, redirecionar
    const token = localStorage.getItem('familyToken');
    if (token) {
      navigate('/familia');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Criar um novo AbortController para a requisição
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${API_URL}/api/dnaconnect/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
        signal: controller.signal,
        cache: 'no-store'
      });

      clearTimeout(timeoutId);

      // Verificar status primeiro
      if (response.status === 401) {
        throw new Error('Credenciais inválidas');
      }
      
      if (response.status === 403) {
        throw new Error('Acesso bloqueado. Entre em contato com a escola.');
      }
      
      if (!response.ok) {
        throw new Error('Erro ao fazer login. Tente novamente.');
      }

      // Ler body como texto e parsear manualmente
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Parse error:', parseError);
        throw new Error('Erro de comunicação com o servidor.');
      }

      // Salvar token e dados do usuário
      localStorage.setItem('familyToken', data.access_token);
      localStorage.setItem('familyUser', JSON.stringify(data.user));

      toast.success(`Bem-vindo(a), ${data.user.responsible_name?.split(' ')[0]}!`);

      // Se precisa trocar senha, redirecionar
      if (data.must_change_password) {
        navigate('/familia/trocar-senha');
      } else {
        navigate('/familia');
      }

    } catch (error) {
      console.error('Login error:', error);
      if (error.name === 'AbortError') {
        toast.error('Tempo limite excedido. Tente novamente.');
      } else {
        toast.error(error.message || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Digite seu email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dnaconnect/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = { message: 'Se o email estiver cadastrado, voce recebera instrucoes.' };
      }
      
      toast.success(data.message || 'Se o email estiver cadastrado, você receberá instruções.');
      setShowForgotPassword(false);
    } catch (error) {
      toast.error('Erro ao solicitar recuperação de senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/novo-dna-logo.png" 
            alt="NOVO DNA" 
            className="w-32 h-32 mx-auto mb-4 object-contain drop-shadow-2xl"
          />
          <h1 className="text-3xl font-bold text-white mb-2">DNA CONNECT</h1>
          <p className="text-purple-300">Portal das Famílias</p>
        </div>

        {/* Card de Login */}
        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">
              {showForgotPassword ? 'Recuperar Senha' : 'Acessar Portal'}
            </CardTitle>
            <CardDescription>
              {showForgotPassword 
                ? 'Digite seu email para receber instruções'
                : 'Entre com suas credenciais'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={showForgotPassword ? handleForgotPassword : handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu.email@exemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {!showForgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 h-12"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {showForgotPassword ? 'Enviar Instruções' : 'Entrar'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => setShowForgotPassword(!showForgotPassword)}
                className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
              >
                {showForgotPassword ? 'Voltar ao login' : 'Esqueci minha senha'}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-purple-300 text-sm mt-8">
          NOVO DNA © 2026<br/>
          <span className="text-purple-400">Muitas mentes, uma missão!</span>
        </p>
      </div>
    </div>
  );
}
